import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";

type ViewState =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "already" }
  | { kind: "invalid"; message: string }
  | { kind: "submitting" }
  | { kind: "success" };

const fnUrl = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/handle-email-unsubscribe`;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid", message: "Missing unsubscribe token." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${fnUrl}?token=${encodeURIComponent(token)}`, { method: "GET" });
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && json.valid === true) {
          setState({ kind: "ready" });
        } else if (json.valid === false && json.reason === "already_unsubscribed") {
          setState({ kind: "already" });
        } else {
          setState({ kind: "invalid", message: json.error || "This unsubscribe link is invalid or expired." });
        }
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "invalid", message: err instanceof Error ? err.message : "Could not verify token." });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setState({ kind: "success" });
      } else if (json.reason === "already_unsubscribed") {
        setState({ kind: "already" });
      } else {
        setState({ kind: "invalid", message: json.error || "Could not process the unsubscribe." });
      }
    } catch (err) {
      setState({ kind: "invalid", message: err instanceof Error ? err.message : "Network error." });
    }
  };

  return (
    <>
    <SeoHead title="Unsubscribe — Excerptus" description="Manage your Excerptus email preferences." noindex />
    <main className="flex min-h-dvh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md shadow-soft">
        <CardHeader>
          <CardTitle>Email preferences</CardTitle>
          <CardDescription>Manage email delivery for this address.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state.kind === "loading" ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying link…
            </div>
          ) : null}

          {state.kind === "ready" ? (
            <>
              <p className="text-sm">
                Click the button below to unsubscribe this email address from future messages.
              </p>
              <Button className="w-full" onClick={() => void handleConfirm()}>
                Confirm unsubscribe
              </Button>
            </>
          ) : null}

          {state.kind === "submitting" ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Processing…
            </div>
          ) : null}

          {state.kind === "success" ? (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <p className="font-medium">You're unsubscribed.</p>
                <p className="text-muted-foreground">This address won't receive further emails from us.</p>
              </div>
            </div>
          ) : null}

          {state.kind === "already" ? (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <p className="font-medium">Already unsubscribed.</p>
                <p className="text-muted-foreground">No further action needed.</p>
              </div>
            </div>
          ) : null}

          {state.kind === "invalid" ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
              <p>{state.message}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </main>
    </>
  );
};

export default Unsubscribe;
